import type { BaseMessage, UserMessage } from '@sendbird/chat/message';
import { getFullName } from '../utils/commonUtils';
import { getLastMessageText } from './common';

export type SendbirdDisplayUser = {
  userId?: string;
  nickname?: string;
  metaData?: Record<string, string>;
};

const ADMIN_ROLE_LABEL = 'YC Admin';

const ROLE_LABELS: Record<string, string> = {
  customer: 'Customer',
};

function capitalizeWord(value: string): string {
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function humanizeToken(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => capitalizeWord(part))
    .join(' ');
}

export function getSendbirdUserMetadata(user?: SendbirdDisplayUser | null): Record<string, string> {
  if (!user) return {};
  const raw = user.metaData ?? (user as { metadata?: Record<string, string> }).metadata ?? {};
  return typeof raw === 'object' && raw !== null ? raw : {};
}

/** Vendor business name from login — uses nested `vendor` for team members, else user name for owner. */
export function getLoggedInVendorDisplayName(
  userDetails?: {
    firstName?: string | null;
    lastName?: string | null;
    vendor?: { firstName?: string | null; lastName?: string | null } | null;
  } | null,
): string | null {
  const vendorName = getFullName(
    userDetails?.vendor?.firstName ?? '',
    userDetails?.vendor?.lastName ?? '',
  );
  if (vendorName) return vendorName;

  const ownerName = getFullName(userDetails?.firstName ?? '', userDetails?.lastName ?? '');
  return ownerName || null;
}

function getSendbirdVendorName(
  metadata?: Record<string, string>,
  options?: SendbirdDisplayOptions,
): string | null {
  const loggedInVendorName = options?.loggedInVendorName?.trim();
  if (loggedInVendorName) return loggedInVendorName;

  const vendorName = metadata?.vendorName?.trim() || metadata?.vendor_name?.trim();
  if (vendorName) return vendorName;

  return options?.channelVendorName?.trim() || null;
}

/** Channel names: `{id} - {vendorName} - {eventName}` (B2C/B2B). */
export function parseVendorNameFromSendbirdChannelName(channelName?: string | null): string | null {
  if (!channelName?.trim()) return null;
  const parts = channelName.split(' - ').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 3) return null;
  return parts[1] || null;
}

export type SendbirdDisplayOptions = {
  /** Naazir login vendor name — preferred over stale Sendbird metadata. */
  loggedInVendorName?: string | null;
  /** Fallback when vendor Sendbird metadata has no vendorName (legacy / not backfilled). */
  channelVendorName?: string | null;
};

export function formatSendbirdRoleLabel(
  metadata?: Record<string, string>,
  options?: SendbirdDisplayOptions,
): string | null {
  const role = metadata?.role?.trim().toLowerCase();
  if (!role) return null;

  if (role === 'admin') {
    return ADMIN_ROLE_LABEL;
  }

  if (role === 'vendor') {
    const vendorName = getSendbirdVendorName(metadata, options);
    if (!vendorName) return null;
    return `${vendorName} Team`;
  }

  return ROLE_LABELS[role] ?? humanizeToken(role);
}

/** In-thread / mention label: `{Name} - YC Admin` or `{Name} - {Vendor Name} Team`. */
export function formatSendbirdSenderNameRole(
  user?: SendbirdDisplayUser | null,
  options?: SendbirdDisplayOptions,
): string {
  const name = user?.nickname?.trim() || 'Unknown';
  const roleLabel = formatSendbirdRoleLabel(getSendbirdUserMetadata(user), options);
  if (!roleLabel || roleLabel === name) return name;
  return `${name} - ${roleLabel}`;
}

export function getSendbirdMessageSender(message: BaseMessage | null | undefined): SendbirdDisplayUser | null {
  if (!message || !('sender' in message)) return null;
  const sender = (message as UserMessage).sender;
  if (!sender) return null;
  return {
    userId: sender.userId,
    nickname: sender.nickname,
    metaData: getSendbirdUserMetadata(sender as SendbirdDisplayUser),
  };
}

/** Inbox snippet for new-model channels: `{Name} - {Role}: {preview}`. */
export function getSendbirdInboxSnippet(
  lastMessage: BaseMessage | null | undefined,
  options?: {
    isNewModelChannel?: boolean;
    currentUserId?: string;
    loggedInVendorName?: string | null;
    channelVendorName?: string | null;
  },
): string {
  const preview = getLastMessageText(lastMessage ?? null);
  if (!preview) return '';

  if (options?.isNewModelChannel !== true) {
    return preview;
  }

  const sender = getSendbirdMessageSender(lastMessage ?? null);
  if (!sender?.userId || sender.userId === options?.currentUserId) {
    return preview;
  }

  const label = formatSendbirdSenderNameRole(sender, {
    loggedInVendorName: options?.loggedInVendorName,
    channelVendorName: options?.channelVendorName,
  });
  return `${label}: ${preview}`;
}
